/*! resize-monitor.js */

import EventTarget from "./event-target";
import Log from "app/utils/log";

const log = new Log("ResizeMonitor");

const REGEXP_DIGITS = /\d+/g;
const BREAKPOINTS = "data-breakpoints";
const BOUND_HANDLERS = [
    "onResize",
    "monitor"
];
const RESIZE_EVENT = "resize";

/**
 * @class ResizeMonitor
 */
class ResizeMonitor extends EventTarget {

    /**
     * @constructs ResizeMonitor
     * @param {Element} element
     */
    constructor(element) {
        super();

        /**
         * @type {Element}
         * @private
         */
        this.el_ = element;
        /**
         * @type {?number}
         * @private
         */
        this.monitorId_ = null;

        BOUND_HANDLERS.forEach((handler) => {
            this[handler] = this[handler].bind(this);
        });

        this.width = null;

        this.wBreakpoints_ = [];

        this.setupBreakpoints();
        this.monitor();
    }

    /**
     * @method
     */
    setupBreakpoints() {
        const attrValue = this.el_.getAttribute(BREAKPOINTS);
        if (attrValue) {
            let points = attrValue.match(REGEXP_DIGITS) || [];
            points = points.map((point) => parseFloat(point));

            this.wBreakpoints_ = this.wBreakpoints_.concat(points).sort();
        }
    }

    /**
     * @method
     * @param {number} currentWidth
     * @returns {number}
     */
    getMinWidth(currentWidth) {
        for (let i = this.wBreakpoints_.length - 1; 0 <= i; i--) {
            const breakpoint = this.wBreakpoints_[i];

            if (currentWidth >= breakpoint) {
                return breakpoint;
            }
        }

        return 0;
    }

    /**
     * @method
     */
    monitor() {
        this.monitorId_ = window.requestAnimationFrame(() => {
            const width = this.el_.clientWidth;

            if (this.width !== width) {
                const minWidth = this.getMinWidth(width);

                this.width = width;
                this.el_.setAttribute("data-min-width", minWidth);
                this.onResize();
            }

            this.monitor();
        });
    }

    /**
     * @method
     */
    stopMonitoring() {
        window.cancelAnimationFrame(this.monitorId_);
        this.monitorId_ = null;
    }

    /**
     * @method
     */
    onResize() {
        // NOTE: BLVS-2359 -- IE 11 would intermittenly throw an error
        // when trying to calculate the client bounding rect
        try {
            const dimensions = this.el_.getBoundingClientRect();
            this.trigger(RESIZE_EVENT, { dimensions });
        } catch (e) {
            log.error("Unable to calculate element bounds");
        }
    }

    /**
     * @override
     */
    dispose() {
        this.el_ = this.width = this.wBreakpoints_ = null;
        this.stopMonitoring();
        super.dispose();
    }
}

export default ResizeMonitor;
